import React, { useEffect, useMemo, useState } from 'react'
import apiClient, { API_ENDPOINTS } from '../../config/api'
import { isSmallShopMode } from '../../utils/smallShopMode'

const DASHBOARD_API = API_ENDPOINTS.DASHBOARD_ANALYTICS

const formatNumber = value => Number(value ?? 0).toLocaleString()
const formatDate = value => value ? new Date(value).toLocaleString(undefined, {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit'
}) : '—'

const TOP_BAR_GRADIENT = 'linear-gradient(135deg, rgba(19, 56, 181, 0.45), rgba(92, 142, 240, 0.22))'
const BLUE_GLASS_GRADIENT = 'linear-gradient(135deg, rgba(11, 94, 215, 0.5), rgba(98, 179, 255, 0.25))'

const useCountUp = (value, duration = 1200) => {
  const [displayValue, setDisplayValue] = useState(0)

  useEffect(() => {
    const target = Number(value ?? 0)
    let frameId
    let startTimestamp = null

    const animate = timestamp => {
      if (startTimestamp === null) {
        startTimestamp = timestamp
      }
      const progress = Math.min((timestamp - startTimestamp) / duration, 1)
      const currentValue = Math.round(target * progress)
      setDisplayValue(currentValue)
      if (progress < 1) {
        frameId = requestAnimationFrame(animate)
      }
    }

    frameId = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(frameId)
  }, [value, duration])

  return displayValue
}

const MetricCard = ({ title, subtitle, icon, value, footer, gradient = TOP_BAR_GRADIENT, delay = 0 }) => {
  const animatedValue = useCountUp(value)
  const cardGradient = gradient
  return (
    <div className="col-xxl-3 col-lg-3 col-md-6 col-sm-6">
      <div
        className="card border-0 text-white position-relative overflow-hidden metric-card metric-card-animate"
        style={{
          minHeight: 120,
          borderRadius: 12,
          background: 'rgba(15, 23, 42, 0.55)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 16px rgba(15, 23, 42, 0.25)',
          '--metric-card-delay': `${delay}s`
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: cardGradient,
            opacity: 0.75
          }}
        />
        <div className="card-body position-relative p-3 d-flex flex-column gap-2">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <p className="text-uppercase mb-0 text-white-50" style={{ fontSize: '0.65rem', letterSpacing: '0.08em' }}>{subtitle}</p>
              <span className="fw-semibold" style={{ fontSize: '0.9rem' }}>{title}</span>
            </div>
            <div
              className="rounded-circle d-flex align-items-center justify-content-center"
              style={{
                width: 32,
                height: 32,
                background: 'rgba(255, 255, 255, 0.12)',
                border: '1px solid rgba(255,255,255,0.2)'
              }}
            >
              <i className={`${icon} fs-6`}></i>
            </div>
          </div>
          <div>
            <h5 className="fw-bold mb-1 count-up-number" style={{ fontSize: '1.25rem' }}>{formatNumber(animatedValue)}</h5>
            {footer && <p className="mb-0" style={{ fontSize: '0.75rem', opacity: 0.7 }}>{footer}</p>}
          </div>
        </div>
      </div>
    </div>
  )
}

const BarChartCard = ({ title, subtitle, data, colors }) => {
  const maxValue = useMemo(() => {
    if (!data.length) {
      return 1
    }
    const max = Math.max(...data.map(item => item.value))
    return Number.isFinite(max) && max > 0 ? max : 1
  }, [data])

  return (
    <div className="card h-100 shadow-sm border-0">
      <div className="card-body">
        <h5 className="card-title">{title} {subtitle && <span className="text-muted fw-normal">| {subtitle}</span>}</h5>
        <div className="d-flex flex-column gap-3">
          {data.map((item, idx) => {
            const percentage = Math.round((item.value / maxValue) * 100)
            return (
              <div key={item.label}>
                <div className="d-flex justify-content-between mb-1">
                  <span className="fw-semibold">{item.label}</span>
                  <span className="text-muted">{formatNumber(item.value)}</span>
                </div>
                <div className="animated-progress-bar" style={{ height: 10, background: '#edf1f7', borderRadius: 999 }}>
                  <div
                    className="animated-progress-indicator"
                    style={{
                      '--target-width': `${percentage}%`,
                      width: `${percentage}%`,
                      height: '100%',
                      borderRadius: 999,
                      background: colors?.[idx] ?? 'linear-gradient(90deg, #0d6efd, #6610f2)'
                    }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

const EmptyState = ({ icon = 'bi bi-eye-slash', message }) => (
  <div className="text-center py-4 text-muted">
    <i className={`${icon} fs-3 mb-2 d-block`}></i>
    <span>{message}</span>
  </div>
)

const SummaryMetricCard = ({ label, value, color, percentage, formatValue }) => (
  <div className="col-md-2 col-6">
    <div className="border rounded p-2 text-center">
      <div className="text-muted small mb-1">{label}</div>
      <div className={`fw-bold mb-2 text-${color}`}>{formatValue ? formatValue(value) : formatNumber(value)}</div>
      <div className="animated-progress-bar" style={{ height: 6, background: '#edf1f7', borderRadius: 999 }}>
        <div
          className="animated-progress-indicator"
          style={{
            '--target-width': `${Math.min(percentage, 100)}%`,
            width: `${Math.min(percentage, 100)}%`,
            height: '100%',
            borderRadius: 999,
            background: color === 'primary' ? 'linear-gradient(90deg, #0d6efd, #43b0ff)' :
                       color === 'success' ? 'linear-gradient(90deg, #20c997, #0d6efd)' :
                       color === 'info' ? 'linear-gradient(90deg, #0dcaf0, #0d6efd)' :
                       color === 'warning' ? 'linear-gradient(90deg, #ffc107, #fd7e14)' :
                       'linear-gradient(90deg, #dc3545, #fd7e14)'
          }}
        />
      </div>
    </div>
  </div>
)

const formatChartDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const SalesTrendChart = ({ data, summary, endDate, onDateChange }) => {
  const breakdown = useMemo(() => data?.breakdown ?? [], [data?.breakdown])
  const maxAmount = useMemo(() => {
    if (!breakdown.length) return 1
    const maxSales = Math.max(...breakdown.map(item => item.total_amount ?? 0))
    const maxProfit = Math.max(...breakdown.map(item => item.total_profit ?? 0))
    const max = Math.max(maxSales, maxProfit)
    return Number.isFinite(max) && max > 0 ? max : 1
  }, [breakdown])

  const chartHeight = 280
  const chartPadding = { top: 20, right: 20, bottom: 40, left: 50 }
  const chartWidth = '100%'

  if (!breakdown.length) {
    return (
      <div className="card h-100 shadow-sm border-0">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="card-title mb-0 d-flex align-items-center gap-2">
              <i className="bi bi-graph-up-arrow text-success"></i>
              Sales Trends
            </h5>
            {onDateChange && (
              <div className="d-flex align-items-center gap-2">
                <label htmlFor="sales-end-date" className="small text-muted mb-0">
                  End Date:
                </label>
                <input
                  type="date"
                  id="sales-end-date"
                  value={endDate || ''}
                  onChange={(e) => onDateChange(e.target.value)}
                  className="form-control form-control-sm"
                  style={{ width: 'auto', minWidth: '150px' }}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
            )}
          </div>

          {/* Summary Section */}
          {summary && (() => {
            const maxValues = {
              total_sales: Math.max(summary.total_sales ?? 0, 1),
              total_amount: Math.max(summary.total_amount ?? 0, 1),
              total_profit: Math.max(summary.total_profit ?? 0, 1),
              total_items_sold: Math.max(summary.total_items_sold ?? 0, 1),
              profit_margin: 100 // Margin is already a percentage
            }
            const allMax = Math.max(maxValues.total_sales, maxValues.total_amount, maxValues.total_profit, maxValues.total_items_sold, 100)
            
            return (
              <div className="row g-3 mb-4">
                <SummaryMetricCard
                  label="Total Sales"
                  value={summary.total_sales ?? 0}
                  color="primary"
                  percentage={(summary.total_sales ?? 0) / allMax * 100}
                />
                <SummaryMetricCard
                  label="Total Amount"
                  value={summary.total_amount ?? 0}
                  color="success"
                  percentage={(summary.total_amount ?? 0) / allMax * 100}
                />
                <SummaryMetricCard
                  label="Total Profit"
                  value={summary.total_profit ?? 0}
                  color="info"
                  percentage={(summary.total_profit ?? 0) / allMax * 100}
                />
                <SummaryMetricCard
                  label="Items Sold"
                  value={summary.total_items_sold ?? 0}
                  color="warning"
                  percentage={(summary.total_items_sold ?? 0) / allMax * 100}
                />
                <SummaryMetricCard
                  label="Profit Margin"
                  value={summary.profit_margin ?? 0}
                  color="danger"
                  percentage={Math.min((summary.profit_margin ?? 0), 100)}
                  formatValue={(val) => `${val.toFixed(2)}%`}
                />
              </div>
            )
          })()}

          <EmptyState message="No sales data available for the selected period." />
        </div>
      </div>
    )
  }

  return (
    <div className="card h-100 shadow-sm border-0">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="card-title mb-0 d-flex align-items-center gap-2">
              <i className="bi bi-graph-up-arrow text-success"></i>
              Sales Trends
            </h5>
            {summary && (
              <small className="text-muted">
                {summary.start_date && summary.end_date && (
                  <>
                    {formatChartDate(summary.start_date)} - {formatChartDate(summary.end_date)}
                  </>
                )}
              </small>
            )}
          </div>
          <div className="d-flex align-items-center gap-3">
            {onDateChange && (
              <div className="d-flex align-items-center gap-2">
                <label htmlFor="sales-end-date" className="small text-muted mb-0">
                  End Date:
                </label>
                <input
                  type="date"
                  id="sales-end-date"
                  value={endDate || ''}
                  onChange={(e) => onDateChange(e.target.value)}
                  className="form-control form-control-sm"
                  style={{ width: 'auto', minWidth: '150px' }}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
            )}
          </div>
        </div>

        {/* Summary Section */}
        {summary && (() => {
          const maxValues = {
            total_sales: Math.max(summary.total_sales ?? 0, 1),
            total_amount: Math.max(summary.total_amount ?? 0, 1),
            total_profit: Math.max(summary.total_profit ?? 0, 1),
            total_items_sold: Math.max(summary.total_items_sold ?? 0, 1),
            profit_margin: 100 // Margin is already a percentage
          }
          const allMax = Math.max(maxValues.total_sales, maxValues.total_amount, maxValues.total_profit, maxValues.total_items_sold, 100)
          
          return (
            <div className="row g-3 mb-4">
              <SummaryMetricCard
                label="Total Sales"
                value={summary.total_sales ?? 0}
                color="primary"
                percentage={(summary.total_sales ?? 0) / allMax * 100}
              />
              <SummaryMetricCard
                label="Total Amount"
                value={summary.total_amount ?? 0}
                color="success"
                percentage={(summary.total_amount ?? 0) / allMax * 100}
              />
              <SummaryMetricCard
                label="Total Profit"
                value={summary.total_profit ?? 0}
                color="info"
                percentage={(summary.total_profit ?? 0) / allMax * 100}
              />
              <SummaryMetricCard
                label="Items Sold"
                value={summary.total_items_sold ?? 0}
                color="warning"
                percentage={(summary.total_items_sold ?? 0) / allMax * 100}
              />
              <SummaryMetricCard
                label="Profit Margin"
                value={summary.profit_margin ?? 0}
                color="danger"
                percentage={Math.min((summary.profit_margin ?? 0), 100)}
                formatValue={(val) => `${val.toFixed(2)}%`}
              />
            </div>
          )
        })()}

        <div style={{ position: 'relative', height: chartHeight, width: chartWidth }}>
          {/* Y-axis labels */}
          <div style={{ position: 'absolute', left: 0, top: chartPadding.top, bottom: chartPadding.bottom, width: 50 }}>
            {[0, 0.25, 0.5, 0.75, 1].map((ratio, idx) => {
              const value = maxAmount * (1 - ratio)
              return (
                <div
                  key={idx}
                  style={{
                    position: 'absolute',
                    top: `${ratio * 100}%`,
                    transform: 'translateY(-50%)',
                    fontSize: '0.75rem',
                    color: '#6c757d',
                    width: '100%',
                    textAlign: 'right',
                    paddingRight: '8px'
                  }}
                >
                  {formatNumber(Math.round(value))}
                </div>
              )
            })}
          </div>

          {/* Chart area */}
          <div
            style={{
              position: 'absolute',
              left: chartPadding.left,
              top: chartPadding.top,
              right: chartPadding.right,
              bottom: chartPadding.bottom,
              borderLeft: '1px solid #e9ecef',
              borderBottom: '1px solid #e9ecef'
            }}
          >
            <svg width="100%" height="100%" style={{ overflow: 'visible' }}>
              {/* Grid lines */}
              {[0, 0.25, 0.5, 0.75, 1].map((ratio, idx) => (
                <line
                  key={idx}
                  x1="0"
                  y1={`${ratio * 100}%`}
                  x2="100%"
                  y2={`${ratio * 100}%`}
                  stroke="#e9ecef"
                  strokeWidth="1"
                  strokeDasharray="2,2"
                />
              ))}

              {/* Area path */}
              {breakdown.length > 0 && (() => {
                const salesPoints = breakdown.map((item, idx) => {
                  const x = (idx / (breakdown.length - 1 || 1)) * 100
                  const y = 100 - ((item.total_amount ?? 0) / maxAmount) * 100
                  return `${x},${y}`
                }).join(' ')

                const profitPoints = breakdown.map((item, idx) => {
                  const x = (idx / (breakdown.length - 1 || 1)) * 100
                  const y = 100 - ((item.total_profit ?? 0) / maxAmount) * 100
                  return `${x},${y}`
                }).join(' ')

                return (
                  <>
                    <defs>
                      <linearGradient id="salesGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="rgba(13, 110, 253, 0.3)" />
                        <stop offset="100%" stopColor="rgba(13, 110, 253, 0.05)" />
                      </linearGradient>
                      <linearGradient id="profitGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="rgba(32, 201, 151, 0.3)" />
                        <stop offset="100%" stopColor="rgba(32, 201, 151, 0.05)" />
                      </linearGradient>
                    </defs>
                    {/* Sales Amount Area */}
                    <path
                      d={`M ${salesPoints.split(' ')[0]} L ${salesPoints} L 100,100 L 0,100 Z`}
                      fill="url(#salesGradient)"
                      style={{ transition: 'all 0.6s ease' }}
                    />
                    {/* Sales Amount Line */}
                    <path
                      d={`M ${salesPoints}`}
                      fill="none"
                      stroke="#0d6efd"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ transition: 'all 0.6s ease' }}
                    />
                    {/* Total Profit Area */}
                    <path
                      d={`M ${profitPoints.split(' ')[0]} L ${profitPoints} L 100,100 L 0,100 Z`}
                      fill="url(#profitGradient)"
                      style={{ transition: 'all 0.6s ease' }}
                    />
                    {/* Total Profit Line */}
                    <path
                      d={`M ${profitPoints}`}
                      fill="none"
                      stroke="#20c997"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ transition: 'all 0.6s ease' }}
                    />
                    {/* Data points for Sales Amount */}
                    {breakdown.map((item, idx) => {
                      const x = (idx / (breakdown.length - 1 || 1)) * 100
                      const y = 100 - ((item.total_amount ?? 0) / maxAmount) * 100
                      return (
                        <circle
                          key={`sales-${idx}`}
                          cx={`${x}%`}
                          cy={`${y}%`}
                          r="5"
                          fill="#0d6efd"
                          stroke="#fff"
                          strokeWidth="2"
                          style={{ transition: 'all 0.6s ease', cursor: 'pointer' }}
                        />
                      )
                    })}
                    {/* Data points for Total Profit */}
                    {breakdown.map((item, idx) => {
                      const x = (idx / (breakdown.length - 1 || 1)) * 100
                      const y = 100 - ((item.total_profit ?? 0) / maxAmount) * 100
                      return (
                        <circle
                          key={`profit-${idx}`}
                          cx={`${x}%`}
                          cy={`${y}%`}
                          r="5"
                          fill="#20c997"
                          stroke="#fff"
                          strokeWidth="2"
                          style={{ transition: 'all 0.6s ease', cursor: 'pointer' }}
                        />
                      )
                    })}
                  </>
                )
              })()}
            </svg>

            {/* X-axis labels */}
            <div style={{ position: 'absolute', bottom: -35, left: 0, right: 0, display: 'flex', justifyContent: 'space-between' }}>
              {breakdown.map((item, idx) => (
                <div
                  key={idx}
                  style={{
                    fontSize: '0.7rem',
                    color: '#6c757d',
                    transform: idx === 0 ? 'translateX(0)' : idx === breakdown.length - 1 ? 'translateX(-100%)' : 'translateX(-50%)',
                    textAlign: idx === 0 ? 'left' : idx === breakdown.length - 1 ? 'right' : 'center',
                    flex: 1
                  }}
                >
                  {formatChartDate(item.date)}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="d-flex justify-content-center gap-4 mt-3 pt-3 border-top">
          <div className="d-flex align-items-center gap-2">
            <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#0d6efd' }}></div>
            <small className="text-muted">Sales Amount</small>
          </div>
          <div className="d-flex align-items-center gap-2">
            <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#20c997' }}></div>
            <small className="text-muted">Total Profit</small>
          </div>
        </div>
      </div>
    </div>
  )
}

const Dashboard = () => {
  const smallShopMode = isSmallShopMode()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [deadStockState, setDeadStockState] = useState(null)
  const [deadStockLoading, setDeadStockLoading] = useState(false)
  const [slowStockState, setSlowStockState] = useState(null)
  const [slowStockLoading, setSlowStockLoading] = useState(false)
  const [salesHistory, setSalesHistory] = useState(null)
  const [salesHistoryLoading, setSalesHistoryLoading] = useState(false)
  const [salesEndDate, setSalesEndDate] = useState(() => {
    const today = new Date()
    return today.toISOString().split('T')[0] // YYYY-MM-DD format
  })
  const [salesStartDate, setSalesStartDate] = useState(() => {
    const today = new Date()
    const weekAgo = new Date(today)
    weekAgo.setDate(today.getDate() - 7) // 7 days ago
    return weekAgo.toISOString().split('T')[0] // YYYY-MM-DD format
  })

  useEffect(() => {
    const controller = new AbortController()

    const fetchDashboard = async () => {
      setLoading(true)
      try {
        const response = await apiClient.get(DASHBOARD_API, { signal: controller.signal })
        setData(response.data)
        setDeadStockState(response.data?.dead_stock ?? null)
        setSlowStockState(response.data?.slow_stock ?? null)
        setError(null)
      } catch (err) {
        if (!controller.signal.aborted) {
          setError('We could not load the dashboard right now. Please refresh to try again.')
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false)
        }
      }
    }

    fetchDashboard()
    return () => controller.abort()
  }, [])

  useEffect(() => {
    const controller = new AbortController()

    const fetchSalesHistory = async () => {
      setSalesHistoryLoading(true)
      try {
        const response = await apiClient.get(API_ENDPOINTS.SALES_HISTORY, {
          signal: controller.signal,
          params: { 
            start_date: salesStartDate,
            end_date: salesEndDate 
          }
        })
        setSalesHistory(response.data)
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error('Failed to load sales history:', err)
        }
      } finally {
        if (!controller.signal.aborted) {
          setSalesHistoryLoading(false)
        }
      }
    }

    fetchSalesHistory()
    return () => controller.abort()
  }, [salesEndDate, salesStartDate])

  const summary = data?.summary ?? {}
  const stock = data?.stock ?? {}
  const users = data?.users ?? {}
  const recentlyAdded = data?.recently_added_products ?? []
  const deadStock = deadStockState ?? data?.dead_stock ?? {}
  const slowStock = slowStockState ?? data?.slow_stock ?? {}

  const handleDeadStockPeriodClick = async (period) => {
    if (!period || period === deadStock?.selected_period || deadStockLoading) return

    // Optimistically update selected period so the chip highlights immediately
    setDeadStockState(prev => ({
      ...(prev || deadStock || {}),
      selected_period: period
    }))

    try {
      setDeadStockLoading(true)
      const response = await apiClient.get(DASHBOARD_API, {
        // Support multiple possible param names the backend may expect
        params: { dead_stock_period: period, period }
      })
      setData(prev => ({ ...(prev || {}), ...response.data }))
      setDeadStockState(response.data?.dead_stock ?? null)
    } catch (e) {
      // If the request fails, fall back to existing state
      setDeadStockState(prev => prev || deadStock)
    } finally {
      setDeadStockLoading(false)
    }
  }

  const handleSlowStockPeriodClick = async (period) => {
    if (!period || period === slowStock?.selected_period || slowStockLoading) return

    // Optimistically update selected period so the chip highlights immediately
    setSlowStockState(prev => ({
      ...(prev || slowStock || {}),
      selected_period: period
    }))

    try {
      setSlowStockLoading(true)
      const response = await apiClient.get(DASHBOARD_API, {
        // Support multiple possible param names the backend may expect
        params: { slow_stock_period: period, period }
      })
      setData(prev => ({ ...(prev || {}), ...response.data }))
      setSlowStockState(response.data?.slow_stock ?? null)
    } catch (e) {
      // If the request fails, fall back to existing state
      setSlowStockState(prev => prev || slowStock)
    } finally {
      setSlowStockLoading(false)
    }
  }

  const stockChartData = useMemo(() => {
    const chartItems = [
      { label: 'Branch Stock', value: stock.branch_stock_count ?? 0 }
    ]
    if (!smallShopMode) {
      chartItems.unshift({ label: 'Warehouse Stock', value: stock.warehouse_stock_count ?? 0 })
    }
    return chartItems
  }, [stock.branch_stock_count, stock.warehouse_stock_count, smallShopMode])

  const userChartData = useMemo(() => ([
    { label: 'Active', value: users.active ?? 0 },
    { label: 'Inactive', value: users.inactive ?? 0 }
  ]), [users.active, users.inactive])

  const renderContent = () => {
    if (loading) {
      return (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading dashboard...</span>
          </div>
        </div>
      )
    }

    if (error) {
      return (
        <div className="alert alert-danger d-flex align-items-center" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </div>
      )
    }

    const metricCards = [
      {
        key: 'branches',
        title: 'Branches',
        subtitle: 'Total',
        icon: 'bi bi-building',
        value: summary.total_branches,
        footer: 'Active retail locations',
        gradient: TOP_BAR_GRADIENT,
        delay: 0
      },
      ...(!smallShopMode ? [{
        key: 'warehouses',
        title: 'Warehouses',
        subtitle: 'Distribution hubs',
        icon: 'bi bi-house-gear',
        value: summary.total_warehouses,
        footer: 'Central & regional facilities',
        gradient: TOP_BAR_GRADIENT,
        delay: 0.05
      }] : []),
      {
        key: 'products',
        title: 'Products',
        subtitle: 'Catalog size',
        icon: 'bi bi-box-seam',
        value: summary.total_products,
        footer: 'Tracked SKUs ',
        gradient: BLUE_GLASS_GRADIENT,
        delay: 0.1
      },
      {
        key: 'stock',
        title: 'Stock Records',
        subtitle: 'Inventory depth',
        icon: 'bi bi-boxes',
        value: stock.total_stock_records,
        footer: smallShopMode ? 'Branch stock entries' : 'Branch + warehouse entries',
        gradient: BLUE_GLASS_GRADIENT,
        delay: 0.15
      }
    ]

    return (
      <>
        <div className="row g-4">
          {metricCards.map(card => (
            <MetricCard key={card.key} {...card} />
          ))}
        </div>

        <div className="row g-4 mt-1">
          <div className="col-12">
            {salesHistoryLoading ? (
              <div className="card shadow-sm border-0">
                <div className="card-body">
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading sales trends...</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <SalesTrendChart
                data={salesHistory}
                summary={salesHistory?.summary}
                endDate={salesEndDate}
                onDateChange={(newEndDate) => {
                  setSalesEndDate(newEndDate)
                  // Automatically set start date to 7 days before the new end date
                  if (newEndDate) {
                    const endDate = new Date(newEndDate)
                    const startDate = new Date(endDate)
                    startDate.setDate(endDate.getDate() - 7)
                    setSalesStartDate(startDate.toISOString().split('T')[0])
                  }
                }}
              />
            )}
          </div>
        </div>

        <div className="row g-4 mt-1">
          <div className="col-lg-6">
            <BarChartCard
              title="Stock Distribution"
              subtitle="By location"
              data={stockChartData}
              colors={['linear-gradient(90deg,#0d6efd,#43b0ff)', 'linear-gradient(90deg,#20c997,#0d6efd)']}
            />
          </div>
          <div className="col-lg-6">
            <BarChartCard
              title="User Status"
              subtitle="Activity snapshot"
              data={userChartData}
              colors={['linear-gradient(90deg,#6610f2,#d63384)', 'linear-gradient(90deg,#adb5bd,#6c757d)']}
            />
          </div>
        </div>

        <div className="row g-4 mt-1">
          <div className="col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h5 className="card-title mb-0 d-flex align-items-center gap-2">
                    <i className="bi bi-stars text-primary" />
                    Recently Added Products
                  </h5>
                  <span className="badge rounded-pill bg-primary bg-opacity-10 text-primary">
                    {recentlyAdded.length} new
                  </span>
                </div>
                <p className="text-muted small mb-2">
                  Latest items introduced into your catalog, ordered by creation date.
                </p>
                {recentlyAdded.length ? (
                  <div className="table-responsive mt-3">
                    <table className="table align-middle mb-0 table-hover">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '45%' }}>Product</th>
                          {/* <th style={{ width: '25%' }}>SKU</th> */}
                          <th style={{ width: '30%' }}>Added</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentlyAdded.map(item => (
                          <tr key={item.id}>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <div className="rounded-circle d-flex align-items-center justify-content-center bg-primary bg-opacity-10 text-primary" style={{ width: 32, height: 32 }}>
                                  <i className="bi bi-box-seam" />
                                </div>
                                <div>
                                  <div className="fw-semibold">{item.name}</div>
                                </div>
                              </div>
                            </td>
                            {/* <td>
                              <span className="badge bg-primary bg-opacity-10 text-primary">
                                {item.sku}
                              </span>
                            </td> */}
                            <td>
                              <small className="text-muted d-block">{formatDate(item.created_at)}</small>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <EmptyState message="No products were added in the last window." />
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div>
                    <h5 className="card-title mb-0 d-flex align-items-center gap-2">
                      <i className="bi bi-archive text-danger" />
                      Dead Stock
                    </h5>
                    <small className="text-muted">
                      Period: {deadStock.selected_period?.toUpperCase()}
                    </small>
                  </div>
                  <div className="d-flex flex-wrap gap-1 justify-content-end">
                    {deadStock.available_periods?.map(period => {
                      const isActive = period === deadStock.selected_period
                      return (
                        <span
                          key={period}
                          role="button"
                          onClick={() => handleDeadStockPeriodClick(period)}
                          className={`badge rounded-pill ${
                            isActive ? 'bg-danger text-white' : 'bg-light text-dark'
                          }`}
                          style={{
                            cursor: isActive || deadStockLoading ? 'default' : 'pointer',
                            opacity: deadStockLoading && isActive ? 0.7 : 1
                          }}
                        >
                          {period.toUpperCase()}
                        </span>
                      )
                    })}
                  </div>
                </div>
                <p className="text-muted small mb-2">
                  Minimum sales required: <strong>{deadStock.minimum_sales_required ?? 0}</strong> units over the
                  selected period.
                </p>
                {deadStock.items?.length ? (
                  <div className="table-responsive mt-3">
                    <table className="table align-middle mb-0 table-hover">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '40%' }}>Product</th>
                          {!smallShopMode && <th>Warehouse</th>}
                          <th>Branch</th>
                          <th>Total</th>
                          <th>Last Sale</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deadStock.items.map(item => (
                          <tr key={item.id}>
                            <td>
                              <div className="fw-semibold d-flex align-items-center gap-2">
                                {/* <span className="badge rounded-pill bg-danger bg-opacity-10 text-danger">
                                  {item.sku}
                                </span> */}
                                <span>{item.name}</span>
                              </div>
                            </td>
                            {!smallShopMode && <td>{formatNumber(item.warehouse_stock)}</td>}
                            <td>{formatNumber(item.branch_stock)}</td>
                            <td>
                              <span className="badge bg-danger bg-opacity-10 text-danger">
                                {formatNumber(item.total_stock)}
                              </span>
                            </td>
                            <td>
                              <small className="text-muted">
                                {item.last_sold_at ? formatDate(item.last_sold_at) : 'No sales yet'}
                              </small>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <EmptyState icon="bi bi-emoji-smile" message="Great news! No dead stock in this period." />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="row g-4 mt-1">
          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="card-title mb-0 d-flex align-items-center gap-2">
                      <i className="bi bi-hourglass-split text-warning"></i>
                      Slow Moving Stock
                    </h5>
                    <small className="text-muted">
                      Period: {slowStock.selected_period?.toUpperCase()}
                    </small>
                  </div>
                  <div className="d-flex flex-column align-items-end gap-2">
                    <div className="d-flex flex-wrap gap-1 justify-content-end">
                      {slowStock.available_periods?.map(period => {
                        const isActive = period === slowStock.selected_period
                        return (
                          <span
                            key={period}
                            role="button"
                            onClick={() => handleSlowStockPeriodClick(period)}
                            className={`badge rounded-pill ${
                              isActive ? 'bg-warning text-dark' : 'bg-light text-dark'
                            }`}
                            style={{
                              cursor: isActive || slowStockLoading ? 'default' : 'pointer',
                              opacity: slowStockLoading && isActive ? 0.7 : 1
                            }}
                          >
                            {period.toUpperCase()}
                          </span>
                        )
                      })}
                    </div>
                    <small className="text-muted">
                      Sales range: {slowStock.minimum_sales_threshold ?? 0} - {slowStock.maximum_sales_threshold ?? 0}
                    </small>
                  </div>
                </div>
                <p className="text-muted small mb-2">
                  Products with sales between <strong>{slowStock.minimum_sales_threshold ?? 0}</strong> and{' '}
                  <strong>{slowStock.maximum_sales_threshold ?? 0}</strong> units over the selected period.
                </p>
                {slowStock.items?.length ? (
                  <div className="table-responsive mt-3">
                    <table className="table align-middle mb-0 table-hover">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '40%' }}>Product</th>
                          <th>Sold In Period</th>
                          {!smallShopMode && <th>Warehouse</th>}
                          <th>Branch</th>
                          <th>Last Sale</th>
                        </tr>
                      </thead>
                      <tbody>
                        {slowStock.items.map(item => (
                          <tr key={item.id}>
                            <td>
                              <div className="fw-semibold d-flex align-items-center gap-2">
                                <span>{item.name}</span>
                              </div>
                              <small className="text-muted">{item.sku}</small>
                            </td>
                            <td>
                              <span className="badge bg-warning bg-opacity-10 text-warning">
                                {formatNumber(item.sold_in_period)}
                              </span>
                            </td>
                            {!smallShopMode && <td>{formatNumber(item.warehouse_stock)}</td>}
                            <td>{formatNumber(item.branch_stock)}</td>
                            <td>
                              <small className="text-muted">
                                {item.last_sold_at ? formatDate(item.last_sold_at) : 'No sales yet'}
                              </small>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <EmptyState icon="bi bi-check-circle" message="No slow moving stock detected for this period." />
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <div className="pagetitle">
        <h1>Dashboard</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item active">Dashboard</li>
          </ol>
        </nav>
      </div>

      <section className="section dashboard">
        {renderContent()}
      </section>
    </>
  )
}

export default Dashboard


